import React, { ChangeEvent } from "react";
import { FormikProps, FieldArrayRenderProps } from "formik";
import { formikFieldArrayProps } from "../../../framework/formUtils";
import { useTranslation } from "react-i18next";

import { styled } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import MuiSelect from "../../../framework/muiSelect";

import { handleApiError } from "../../../../services/baseService";
import { useConfirm, ConfirmType } from "../../../../hooks/framework/useConfirm";
import { ProductForm } from "./productTable";
import { TransportOrderProductDetails } from "../../../../models/transport/transportOrderProductDetails";
import { ProductItem } from "../../../../models/product/product";
import { TransportOrderDetailsDto } from "../../../../models/transport/transportOrder";

export interface ProductRowsProps {
    formikProps: FormikProps<ProductForm>;
    arrayHelpers: FieldArrayRenderProps;
    changesDisabled?: boolean;
    ownerProducts: ProductItem[];
    order: TransportOrderDetailsDto;
    orderAddedProducts?: string[];
}

const StyledTextField = styled(TextField)(() => ({
    "input::-webkit-outer-spin-button, input::-webkit-inner-spin-button": {
        "WebkitAppearance": "none",
        "margin": 0
    },
    "input[type=number]": {
        "MozAppearance": "textfield"
    }
}));

export const ProductRows = ({ formikProps, arrayHelpers, changesDisabled, ownerProducts, orderAddedProducts }: ProductRowsProps) => {
    const { t } = useTranslation();
    const confirm = useConfirm();
    const submit = () => void formikProps?.submitForm().catch(handleApiError);
    const handleTypeChange = (e: ChangeEvent<HTMLSelectElement>, i: number) => {
        void formikProps.setFieldValue(
            `rows.${i}.productId`,
            e.target.value
        );
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        setTimeout(() => submit());
    };

    const inputProps = {
        min: 0,
        style: {
            fontSize: "0.9rem",
        }
    };

    return (
        <>
            { formikProps?.values?.rows?.map((row: TransportOrderProductDetails, i: number) => {
                const selectedProduct = ownerProducts.find(p => p.id === row.productId);
                return (
                    <TableRow
                        key={`productTable-row-${row.key}`}
                        data-testid={`productTable-row-${i}`}
                    >
                        <TableCell sx={{ width: "5%" }}>
                            { i + 1 }
                        </TableCell>

                        <TableCell sx={{ width: "60%" }}>
                            <MuiSelect
                                {...formikFieldArrayProps(formikProps, "productId", i)}
                                options={
                                    ownerProducts.length > 0 &&
                                    ownerProducts?.map(product => ({ id: product.id, title: product.name }))
                                }
                                defaultValue=""
                                value={
                                    (row?.productId === undefined ||
                                    row?.productId === null ||
                                    ownerProducts.length === 0) ? "" : `${row?.productId}`
                                }
                                onChange={(e) => handleTypeChange(e, i)}
                                fontSize="0.9rem"
                                disabled={changesDisabled}
                                disabledItems={orderAddedProducts}
                            />
                        </TableCell>
                        <TableCell sx={{ width: "40%" }}>
                            <StyledTextField
                                {...formikFieldArrayProps(formikProps, "amount", i)}
                                type="number"
                                fullWidth
                                inputProps={inputProps}
                                disabled={changesDisabled}
                                InputProps={{
                                    endAdornment: selectedProduct?.unitName && (
                                        <InputAdornment position="end" sx={{ color: "text.secondary" }}>
                                            {selectedProduct.unitName}
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </TableCell>
                        <TableCell align="center">
                            <DeleteIcon
                                color={changesDisabled ? "disabled" : "error"}
                                onClick={() => {
                                    if(!changesDisabled) {
                                        if (row.transportOrderId || (row.productId && row.amount)) {
                                            confirm({
                                                icon: DeleteIcon,
                                                title: t("transport.order.deleteProductRowConfirmation",
                                                    { name: ownerProducts?.find((p) => p.id === row.productId)?.name ?? "" }
                                                ),
                                                submitText: t("common.continue"),
                                                cancelText: t("common.cancel"),
                                                type: ConfirmType.Remove,
                                                onSubmit: () => {
                                                    arrayHelpers.remove(i);
                                                    submit();
                                                }
                                            });
                                        } else {
                                            arrayHelpers.remove(i);
                                            submit();
                                        }
                                    }
                                }}
                                data-testid={`productTable-row-delete-${i}`}
                            />
                        </TableCell>
                    </TableRow>
                );
            })}
        </>
    );
};
